import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Presentation from '../../components/Presentation/Presentation';
import MyCarousel from '../../components/Carousel/Carousel';
import VracTuto from '../../components/VracTuto/VracTuto';
import { useScrollAnimation } from '../../lib/customHooks';

import './Home.scss';

import carouselData from '../../data/carousel.json';
const FALLBACK_CAROUSEL = carouselData.photos.map(f => `/assets/Home/Carousel/${f}`);

// Si une photo distante (Vercel Blob) ne charge pas, on retombe sur la version
// locale du même fichier (ex: "photoMagasin3-abc123.webp" -> "photoMagasin3.webp"),
// pour rester indépendant d'un éventuel blocage du store Blob.
function getLocalFallback(url, index) {
  const match = url && url.match(/photoMagasin(\d+)/i);
  if (match) {
    const local = `/assets/Home/Carousel/photoMagasin${match[1]}.webp`;
    if (carouselData.photos.includes(`photoMagasin${match[1]}.webp`)) return local;
  }
  return FALLBACK_CAROUSEL[index % FALLBACK_CAROUSEL.length];
}

const CATEGORIES = [
  {
    titre: 'Épicerie salée',
    texte: 'Pâtes, riz, légumineuses, céréales, fruits secs, huiles et condiments.',
    icone: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><rect x="12" y="14" width="24" height="28" rx="5"/><rect x="14" y="8" width="20" height="6" rx="2"/><path d="M17 30c3-3 6 3 9 0s6 3 9 0M17 36c3-3 6 3 9 0s6 3 9 0"/></svg>
    ),
  },
  {
    titre: 'Épicerie sucrée',
    texte: 'Biscuits, chocolats, sucres, thés et cafés, confiseries.',
    icone: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="15"/><circle cx="18" cy="20" r="1.6" fill="currentColor"/><circle cx="28" cy="17" r="1.6" fill="currentColor"/><circle cx="30" cy="28" r="1.6" fill="currentColor"/><circle cx="20" cy="30" r="1.6" fill="currentColor"/><circle cx="25" cy="24" r="1.6" fill="currentColor"/></svg>
    ),
  },
  {
    titre: 'Produits frais',
    texte: 'Œufs, fromages, légumes de saison, produits laitiers et locaux.',
    icone: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 42c-6 0-10-5-10-12s7-16 10-16 10 9 10 16-4 12-10 12z"/><path d="M24 14V6M24 6c3 0 6 2 7 5M24 6c-3 0-6 2-7 5"/></svg>
    ),
  },
  {
    titre: 'Entretien & hygiène',
    texte: 'Lessive, savons, shampoings solides, brosses et accessoires.',
    icone: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><rect x="15" y="18" width="18" height="24" rx="4"/><path d="M24 18v-6M20 12h8M24 12V8h7"/><path d="M19 30h10"/></svg>
    ),
  },
];

function AnimatedSection({ children, className = '', delay = 0 }) {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`animate-on-scroll ${isVisible ? 'visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function Home() {
  const [picturesCarousel, setPicturesCarousel] = useState(FALLBACK_CAROUSEL);
  const location = useLocation();

  useEffect(() => { document.title = 'BAsique — Épicerie bio en vrac à Roanne'; }, []);

  useEffect(() => {
    fetch('/api/carousel')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && Array.isArray(data.photos) && data.photos.length > 0) {
          setPicturesCarousel(data.photos.map(p => p.url));
        }
      })
      .catch(() => {});
  }, []);

  // Arrivée avec une ancre (ex: /#vrac depuis une autre page)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className='home-container'>
        <Presentation />

        <AnimatedSection>
          <h2 className="titre-home-cursive">BAsique...</h2>
        </AnimatedSection>

        <AnimatedSection delay={100}>
          <p className='paragraphe-basique'>
              C'est une épicerie <span className="cursive">bio</span> en <span className="cursive">cœur de ville </span>
              offrant avant tout des produits en <span className="cursive">vrac</span>, avec un objectif : vous proposer un large choix de
              produits pour y faire <span className="cursive">la plus grande partie de vos courses courantes</span>, tout en adoptant des
              pratiques de consommation <span className="cursive">zéro déchet</span>.
          </p>
        </AnimatedSection>

        <AnimatedSection delay={200}>
          <MyCarousel pictures={picturesCarousel} getFallback={getLocalFallback} />
        </AnimatedSection>

        <AnimatedSection>
          <h2 className='titre-home' id='produits'>Quels produits ?</h2>
        </AnimatedSection>

        <AnimatedSection delay={100}>
          <p className='paragraphe-basique'>
              <span className="cursive">Epicerie salée, sucrée, produits frais</span>, mais aussi <span className="cursive">produits d'entretien et d'hygiène</span>,
              <span className="cursive"> BAsique</span> entend <span className="cursive">simplifier</span> vos courses tout en vous permettant de
              <span className="cursive"> consommer de façon responsable !</span>
          </p>
        </AnimatedSection>

        <AnimatedSection delay={200}>
          <div className='cats'>
            {CATEGORIES.map((cat) => (
              <article className='cat' key={cat.titre}>
                <div className='cat-icon'>{cat.icone}</div>
                <h3>{cat.titre}</h3>
                <p>{cat.texte}</p>
              </article>
            ))}
          </div>
        </AnimatedSection>

        <div className='vrac-band' id='vrac'>
          <div className='vrac-inner'>
            <AnimatedSection>
              <h2 className='titre-home'>Et tout ça, en vrac !</h2>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <div className='vrac-grid'>
                <div className='vrac-stickers'><img className='sticker sticker--vrac' src='/assets/vrac-icon.webp' alt='Vrac' /></div>
                <p className='paragraphe-basique'>
                    Pour <span className="cursive">se servir en vrac</span>, l'idéal est de venir avec <span className="cursive">ses propres contenants</span> (bocaux, sachets en tissus...),
                    mais pas de panique, il y a <span className="cursive">tout ce qu'il faut</span> en boutique en cas de besoin !
                </p>
                <div className='vrac-stickers'><img className='sticker sticker--local' src='/assets/local-icon.webp' alt='Local' /></div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <VracTuto />
            </AnimatedSection>
          </div>
        </div>
    </div>
  );
}

export default Home;
